import { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";

export default function TelegramSettings() {
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [tokenModified, setTokenModified] = useState(false);
  const originalMaskedToken = useRef("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-telegram-config", {
        method: "GET",
      });
      if (!error && data) {
        const masked = data.bot_token || "";
        setBotToken(masked);
        setChatId(data.chat_id || "");
        setEnabled(data.enabled ?? false);
        originalMaskedToken.current = masked;
        setTokenModified(false);
      }
    } catch (e) {
      console.error("Failed to load Telegram config:", e);
    }
    setLoading(false);
  };

  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    const hasRealToken = botToken.trim() && !botToken.includes("...");

    if (!hasRealToken && !originalMaskedToken.current) {
      showToast("Bot Token을 입력해주세요.", "error");
      return;
    }
    if (!chatId.trim()) {
      showToast("Chat ID를 입력해주세요.", "error");
      return;
    }

    setSaving(true);
    try {
      const body: Record<string, any> = { chat_id: chatId.trim(), enabled };
      if (tokenModified && botToken.trim()) {
        body.bot_token = botToken.trim();
      }

      const { data, error } = await supabase.functions.invoke("manage-telegram-config", {
        method: "PUT",
        body,
      });
      if (error) {
        showToast(error.message || "저장에 실패했습니다.", "error");
      } else {
        await loadConfig();
        if (data?.test_message?.ok === false) {
          showToast(`설정은 저장됐지만 테스트 메시지 발송에 실패했습니다: ${data.test_message.error}`, "error");
        } else if (enabled) {
          showToast("설정이 저장되었습니다. 텔레그램으로 테스트 메시지를 발송했습니다.", "success");
        } else {
          showToast("설정이 저장되었습니다.", "success");
        }
      }
    } catch (e: any) {
      showToast(e.message || "저장에 실패했습니다.", "error");
    }
    setSaving(false);
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBotToken(e.target.value);
    setTokenModified(true);
  };

  const isTokenMasked = botToken.includes("...") && !tokenModified;

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
      {toast && (
        <div
          className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${
            toast.type === "success"
              ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}
        >
          <i
            className={`text-sm ${
              toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"
            }`}
          ></i>
          {toast.msg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
          <i className="ri-loader-4-line animate-spin"></i>
          설정 불러오는 중...
        </div>
      ) : (
        <>
          {/* 헤더 */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-500/10">
              <i className="ri-telegram-line text-sky-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-white font-semibold text-base">텔레그램 알림 연동</h3>
              <p className="text-gray-500 text-xs mt-0.5">
                관리자 알림을 텔레그램 메시지로 실시간 수신합니다
              </p>
            </div>
            {/* 활성화 토글 */}
            <label className="ml-auto relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-white/[0.08] rounded-full peer-checked:bg-sky-500/60 transition-colors peer-focus:ring-2 peer-focus:ring-sky-500/30"></div>
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-gray-400 rounded-full peer-checked:translate-x-5 peer-checked:bg-white transition-transform"></div>
            </label>
          </div>

          {/* 알림 종류 안내 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {[
              { icon: "ri-user-add-line", label: "회원가입", color: "text-emerald-400", bg: "bg-emerald-500/10" },
              { icon: "ri-coin-line", label: "충전 요청", color: "text-amber-400", bg: "bg-amber-500/10" },
              { icon: "ri-smartphone-line", label: "3사 테스트", color: "text-rose-400", bg: "bg-rose-500/10" },
            ].map((item) => (
              <div
                key={item.label}
                className={`${item.bg} border border-white/[0.06] rounded-xl p-3 flex items-center gap-2.5`}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04]">
                  <i className={`${item.icon} ${item.color} text-sm`}></i>
                </div>
                <span className="text-gray-300 text-xs font-medium">{item.label} 알림</span>
                {enabled && (
                  <i className="ri-checkbox-circle-line text-emerald-500 text-xs ml-auto"></i>
                )}
              </div>
            ))}
          </div>

          {/* Bot Token 입력 */}
          <div className="mb-4">
            <label className="block text-gray-300 text-xs font-medium mb-2">
              <i className="ri-key-2-line text-gray-500 mr-1"></i>
              Bot Token
            </label>
            <div className="relative">
              <input
                type="text"
                value={botToken}
                onChange={handleTokenChange}
                placeholder="0000000000:AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA"
                className={`w-full bg-white/[0.05] border rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-sky-500/60 transition-all font-mono ${
                  isTokenMasked ? "border-amber-500/30 bg-amber-500/[0.03]" : "border-white/[0.1]"
                }`}
              />
              {isTokenMasked && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  <span className="text-amber-400/80 text-[10px] font-medium whitespace-nowrap">변경 시 재입력</span>
                </div>
              )}
            </div>
            {isTokenMasked && (
              <p className="text-amber-500/70 text-[10px] mt-1.5 leading-relaxed flex items-center gap-1">
                <i className="ri-information-line text-xs"></i>
                보안을 위해 저장된 토큰은 일부만 표시됩니다. 토큰을 변경할 때만 새로 입력하세요.
              </p>
            )}
            <p className="text-gray-600 text-[10px] mt-1.5 leading-relaxed">
              <a
                href="https://t.me/BotFather"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sky-400 hover:text-sky-300 underline transition-colors"
              >
                @BotFather
              </a>
              에서 새로운 봇을 생성하여 발급받은 토큰을 입력하세요.
            </p>
          </div>

          {/* Chat ID 입력 */}
          <div className="mb-6">
            <label className="block text-gray-300 text-xs font-medium mb-2">
              <i className="ri-chat-3-line text-gray-500 mr-1"></i>
              Chat ID
            </label>
            <input
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="123456789 또는 @channelusername"
              className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-sky-500/60 transition-all"
            />
            <p className="text-gray-600 text-[10px] mt-1.5 leading-relaxed">
              알림을 받을 채팅방의 ID 또는 채널명(@username)을 입력하세요.
              <br />
              <span className="text-gray-700">
                개인 채팅 ID는{" "}
                <a
                  href="https://t.me/userinfobot"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sky-400 hover:text-sky-300 underline transition-colors"
                >
                  @userinfobot
                </a>
                에게 메시지를 보내 확인할 수 있습니다.
              </span>
            </p>
          </div>

          {/* 가이드 */}
          <details className="mb-6 group">
            <summary className="text-gray-500 text-xs cursor-pointer hover:text-gray-400 transition-colors flex items-center gap-1">
              <i className="ri-question-line text-sm"></i>
              연동 가이드 보기
              <i className="ri-arrow-down-s-line text-sm group-open:rotate-180 transition-transform"></i>
            </summary>
            <div className="mt-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 space-y-3">
              <div className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-sky-500/20 text-sky-400 text-[10px] font-bold shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <p className="text-gray-300 text-xs font-medium">봇 생성</p>
                  <p className="text-gray-500 text-[10px] mt-0.5 leading-relaxed">
                    텔레그램에서{" "}
                    <a
                      href="https://t.me/BotFather"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-sky-400 hover:text-sky-300 underline"
                    >
                      @BotFather
                    </a>
                    를 검색 → /newbot 입력 → 봇 이름 설정 → 토큰 발급
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-sky-500/20 text-sky-400 text-[10px] font-bold shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  <p className="text-gray-300 text-xs font-medium">Chat ID 확인</p>
                  <p className="text-gray-500 text-[10px] mt-0.5 leading-relaxed">
                    <a
                      href="https://t.me/userinfobot"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-sky-400 hover:text-sky-300 underline"
                    >
                      @userinfobot
                    </a>
                    에게 아무 메시지나 보내면 ID를 알려줍니다. 또는 생성한 봇에게 먼저 메시지를 보낸 후{" "}
                    <code className="bg-white/[0.06] px-1 py-0.5 rounded text-[10px] text-gray-400">
                      https://api.telegram.org/bot{botToken ? "[TOKEN]" : "YOUR_TOKEN"}/getUpdates
                    </code>{" "}
                    에서 확인할 수 있습니다.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-sky-500/20 text-sky-400 text-[10px] font-bold shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  <p className="text-gray-300 text-xs font-medium">정보 입력 및 활성화</p>
                  <p className="text-gray-500 text-[10px] mt-0.5 leading-relaxed">
                    위 입력란에 Bot Token과 Chat ID를 입력하고 활성화 토글을 켠 뒤 저장하세요. 저장 시 텔레그램으로 테스트 메시지가 발송됩니다.
                  </p>
                </div>
              </div>
            </div>
          </details>

          {/* 저장 버튼 */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm"></i>
                저장 중...
              </>
            ) : (
              <>
                <i className="ri-save-line text-sm"></i>
                설정 저장
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}